// BDSJEA-441/443: preparación PURA (sin React) de las secciones de actividades
// del Historial (Siembras, Ganadería, Tambo) que llegan en GET /resultados
// (claves aditivas `siembras`, `ganaderia`, `tambo`).
//
// Privacidad (contrato 8.2): el backend ya filtra por columna (default-deny), pero
// el front vuelve a proyectar SOLO las columnas permitidas (defensa en
// profundidad: backend viejo/stub/CSV desplazado). Nunca se muestra razón social,
// CUIT, producto ni clave_origen; ninguna columna fuera de la lista llega a la
// pantalla, a las tablas, a los gráficos ni a un eventual export.
import {
  CONTEXTOS_ACTIVIDADES,
  avisosLabels,
  categorizarActividad,
} from "./categorizacionHistorial"
import { motivoVisible } from "./motivoVisible"
import { codigoEntidad } from "./preparacionHistorial"
import { etiquetaResultadoFila, normalizarId } from "./migracionEstadoHelpers"

// Columnas permitidas por sección (= columnasPermitidas del fixture del contrato).
export const COLUMNAS_PERMITIDAS = Object.freeze({
  siembras: Object.freeze([
    "id_entidad",
    "id_plan",
    "id_establecimiento_origen",
    "id_productor",
    "id_establecimiento",
    "resultado",
    "motivo",
    "avisos",
    "codigo_cultivo",
    "codigo_campana",
    "campana_creada",
    "estado_cultivo",
    "fuente_fecha",
    "fuente_rendimiento",
  ]),
  ganaderia: Object.freeze([
    "id_entidad",
    "id_plan",
    "id_cliente",
    "id_productor",
    "id_establecimiento",
    "resultado",
    "motivo",
    "avisos",
    "asignacion",
    "fuente_fecha",
    "filas_creadas",
    "filas_existentes",
    "cabezas_total",
  ]),
  tambo: Object.freeze([
    "id_entidad",
    "id_plan",
    "id_cliente",
    "id_productor",
    "id_establecimiento",
    "id_tambo",
    "destino",
    "resultado",
    "motivo",
    "avisos",
    "activo",
  ]),
  // BDSJEA-468: stock agrícola (columnasPermitidas.stockAgricola del contrato).
  stockAgricola: Object.freeze([
    "id_entidad",
    "id_plan",
    "id_cliente",
    "id_productor",
    "id_establecimiento",
    "columna_origen",
    "codigo_cultivo",
    "cantidad_qq",
    "cantidad_qq_origen",
    "valuacion_total_usd",
    "resultado",
    "motivo",
    "avisos",
    "asignacion",
    "fuente_fecha",
    "origen_precio",
  ]),
})

// Resultados cerrados por paso (vocabularioCerrado.resultado del contrato).
const RESULTADOS = Object.freeze({
  siembras: [
    "CREADO",
    "ACTUALIZADO",
    "SALTADO_EXISTENTE",
    "DESCARTADO",
    "ERROR",
  ],
  ganaderia: ["CREADO", "YA_EXISTIA", "DESCARTADO", "ERROR"],
  tambo: [
    "CREADO",
    "CREADO_HISTORIA",
    "SALTADO_EXISTENTE",
    "DESCARTADO",
    "SIN_DATOS",
    "ERROR",
  ],
  stockAgricola: ["CREADO", "YA_EXISTIA", "DESCARTADO", "ERROR"],
})

export const CLAVE_RESULTADO_OTRO = "OTRO"

const esFila = f => f != null && typeof f === "object"

// id_*: solo dígitos (hasta 18); otro valor => vacío.
const soloId = valor => {
  const id = normalizarId(valor)
  return id == null || String(id).length > 18 ? "" : String(id)
}

/**
 * Proyecta una fila a las columnas permitidas de su sección. `resultado` fuera del
 * vocabulario cerrado => "OTRO"; `id_*` solo dígitos; `motivo` queda como llega
 * (se sanea al mostrarlo con motivoVisible, por lista blanca).
 */
export const proyectarFilaActividad = (fila, nivel) => {
  const permitidas = COLUMNAS_PERMITIDAS[nivel]
  if (!permitidas || !esFila(fila)) return null
  const salida = {}
  permitidas.forEach(col => {
    const valor = fila[col]
    if (valor === undefined || valor === null) return
    if (col.startsWith("id_")) salida[col] = soloId(valor)
    else salida[col] = typeof valor === "string" ? valor : String(valor)
  })
  // Booleanas del contrato: SI/1/true => "true"; NO/0/false => "false"; otro => "".
  ;["campana_creada", "activo"].forEach(col => {
    if (typeof salida[col] === "string") {
      const v = salida[col].trim().toLowerCase()
      if (["si", "1", "true"].includes(v)) salida[col] = "true"
      else if (["no", "0", "false"].includes(v)) salida[col] = "false"
      else salida[col] = ""
    }
  })
  const r = typeof salida.resultado === "string" ? salida.resultado.trim() : ""
  if (r && !RESULTADOS[nivel].includes(r))
    salida.resultado = CLAVE_RESULTADO_OTRO
  return salida
}

export const proyectarFilasActividad = (filas, nivel) =>
  (Array.isArray(filas) ? filas : [])
    .map(f => proyectarFilaActividad(f, nivel))
    .filter(Boolean)

/**
 * Filas listas para las tablas: solo columnas permitidas, resultado legible,
 * motivo SANEADO (etiqueta cerrada) y entidad como código.
 */
export const prepararFilasActividad = (filas, nivel, mapaEntidades) =>
  proyectarFilasActividad(filas, nivel).map(f => ({
    ...f,
    resultado_etiqueta: etiquetaResultadoFila(f.resultado),
    motivo: motivoVisible(f.motivo, nivel),
    avisos_etiqueta: avisosLabels(f.avisos, nivel).join(", "),
    entidad: codigoEntidad(f.id_entidad, mapaEntidades),
  }))

/** Datos del gráfico de torta de una actividad (todo categorizado y cerrado). */
export function datosGraficoActividad(filas, nivel, topN = 4) {
  const r = categorizarActividad(proyectarFilasActividad(filas, nivel), {
    contexto: nivel,
    topN,
  })
  return {
    total: r.total,
    creados: r.creados,
    noImportados: r.noImportados,
    motivos: [
      {
        etiqueta: "Creados (historia)",
        cantidad: r.creadosHistoria,
        fijo: true,
        color: "#17a2b8",
      },
      {
        etiqueta: "Actualizados",
        cantidad: r.actualizados,
        fijo: true,
        color: "#007bff",
      },
      {
        etiqueta: "Ya existían",
        cantidad: r.yaExistian,
        fijo: true,
        color: "#20c997",
      },
      ...r.motivos,
    ].filter(m => m.cantidad > 0),
  }
}

const decimal = v => {
  const n = Number(String(v ?? "").trim())
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/**
 * BDSJEA-468: datos útiles del stock agrícola para la sección (todo sale de columnas cerradas
 * del contrato): QQ importados y excluidos, valuación total (USD) y cuántos registros
 * importados se valuaron con cada origen del precio de valor_agro (o sin parametría).
 */
export function resumenStockAgricola(filas) {
  const r = {
    qqImportados: 0,
    qqExcluidos: 0,
    valuacionTotal: 0,
    origenPrecio: {
      ZONA_DEPARTAMENTO: 0,
      ZONA_PROVINCIA: 0,
      PROMEDIO_ENTRE_ZONAS: 0,
      SIN_PARAMETRIA: 0,
    },
  }
  proyectarFilasActividad(filas, "stockAgricola").forEach(f => {
    const importado = ["CREADO", "YA_EXISTIA"].includes(f.resultado)
    if (!importado) {
      // cantidad_qq va vacía cuando el valor no se pudo cargar (inválido, sobre el tope, fuera de
      // rango): los QQ excluidos salen de cantidad_qq_origen (la cantidad de la fuente), igual que el log.
      r.qqExcluidos += decimal(f.cantidad_qq_origen || f.cantidad_qq)
      return
    }
    r.qqImportados += decimal(f.cantidad_qq)
    r.valuacionTotal += decimal(f.valuacion_total_usd)
    // «Sin parametría» sale del aviso SIN_PARAMETRIA_VALUACION (no de un origen_precio vacío: con
    // --valuacion-stock NULL o en ensayo el origen también está vacío y el aviso lo distingue).
    const avisos = String(f.avisos || "").split(";")
    if (avisos.includes("SIN_PARAMETRIA_VALUACION")) r.origenPrecio.SIN_PARAMETRIA += 1
    else if (f.origen_precio && r.origenPrecio[f.origen_precio] !== undefined)
      r.origenPrecio[f.origen_precio] += 1
  })
  return r
}

export const esContextoActividad = nivel =>
  CONTEXTOS_ACTIVIDADES.includes(nivel)
